import React, { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { ShieldCheck, Robot, Lock, Database, Headset, Globe, ArrowsClockwise } from "@phosphor-icons/react";
import { AdminHeader, Field, inputCls, PrimaryButton } from "@/components/admin/parts";
import { adminApi, getErrorMessage } from "@/lib/api";
import { ResearchSettingsCard } from "@/components/admin/ResearchSettingsCard";
import { VoiceSettingsCard } from "@/components/admin/VoiceSettingsCard";

const BLANK = { email: "", phone: "", hours: "", website: "", note: "" };

function fmtTime(iso) {
  if (!iso) return "Never";
  try {
    return new Date(iso).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

export default function Settings() {
  const [contact, setContact] = useState(BLANK);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // --- OTM website knowledge (Nick's Tier-2 source) ---
  const [web, setWeb] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const pollRef = useRef(null);

  const loadWeb = useCallback(async () => {
    try {
      const { data } = await adminApi.get("/admin/website/status");
      setWeb(data);
      return data;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    adminApi
      .get("/admin/support-contact")
      .then(({ data }) => setContact({ ...BLANK, ...data }))
      .finally(() => setLoading(false));
    loadWeb();
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [loadWeb]);

  // poll while a crawl is running
  useEffect(() => {
    const running = web?.status === "running" || syncing;
    if (running && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        const d = await loadWeb();
        if (d && d.status !== "running") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setSyncing(false);
          if (d.status === "error") toast.error(d.message || "Website sync failed");
          else toast.success(d.message || "Website content refreshed");
        }
      }, 3000);
    }
    if (!running && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, [web, syncing, loadWeb]);

  const syncWebsite = async () => {
    setSyncing(true);
    try {
      const { data } = await adminApi.post("/admin/website/sync");
      setWeb((w) => ({ ...(w || {}), status: "running", message: data.message, progress: 1 }));
    } catch (e) {
      setSyncing(false);
      toast.error(getErrorMessage(e));
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await adminApi.put("/admin/support-contact", contact);
      setContact({ ...BLANK, ...data });
      toast.success("Contact details updated");
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const webRunning = web?.status === "running" || syncing;

  return (
    <div>
      <AdminHeader title="General Settings" subtitle="Configuration and system information" />
      <div className="p-8 max-w-4xl space-y-6">
        <div className="bg-[#0F0F0F] border border-neutral-800 rounded-xl p-6" data-testid="support-contact-card">
          <div className="flex items-center gap-2 mb-1">
            <Headset size={20} className="text-[#73bf44]" weight="duotone" />
            <h2 className="font-heading font-semibold text-white">On The Move Contact Details</h2>
          </div>
          <p className="text-sm text-neutral-500 mb-5">
            Shown to customers on the Contact Support page (alongside "Ask Nick first"). Leave a field blank to hide it.
          </p>
          {loading ? (
            <div className="text-neutral-600 text-sm">Loading...</div>
          ) : (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Support Email">
                  <input value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} className={inputCls} data-testid="contact-email" placeholder="support@onthemovecaravans.com.au" />
                </Field>
                <Field label="Phone">
                  <input value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} className={inputCls} data-testid="contact-phone" placeholder="(07) 0000 0000" />
                </Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Hours">
                  <input value={contact.hours} onChange={(e) => setContact({ ...contact, hours: e.target.value })} className={inputCls} data-testid="contact-hours" placeholder="Mon–Fri, 8:30am – 5:00pm AEST" />
                </Field>
                <Field label="Website">
                  <input value={contact.website} onChange={(e) => setContact({ ...contact, website: e.target.value })} className={inputCls} data-testid="contact-website" placeholder="https://onthemovecaravans.com.au" />
                </Field>
              </div>
              <Field label="Note" hint="Small caption shown under the email">
                <input value={contact.note} onChange={(e) => setContact({ ...contact, note: e.target.value })} className={inputCls} data-testid="contact-note" placeholder="General technical enquiries" />
              </Field>
              <div className="flex justify-end pt-1">
                <PrimaryButton onClick={save} disabled={saving} data-testid="contact-save">{saving ? "Saving..." : "Save Changes"}</PrimaryButton>
              </div>
            </div>
          )}
        </div>

        <div className="bg-[#0F0F0F] border border-neutral-800 rounded-xl p-6" data-testid="website-knowledge-card">
          <div className="flex items-center gap-2 mb-1">
            <Globe size={20} className="text-[#73bf44]" weight="duotone" />
            <h2 className="font-heading font-semibold text-white">Website Knowledge · onthemovecaravans.com.au</h2>
          </div>
          <p className="text-sm text-neutral-500 mb-5">
            Nick reads the official OTM website as a trusted secondary source (after the Knowledge Base) — model pages,
            feature explanations and the how-to video library. Refresh this whenever the website changes.
          </p>
          <div className="flex flex-wrap items-center gap-6 mb-5">
            <div>
              <div className="text-2xl font-heading font-semibold text-white" data-testid="website-page-count">{web?.pages ?? "—"}</div>
              <div className="text-xs text-neutral-500">Pages indexed</div>
            </div>
            <div>
              <div className="text-sm text-white" data-testid="website-last-sync">{fmtTime(web?.last_sync)}</div>
              <div className="text-xs text-neutral-500">Last refreshed</div>
            </div>
            <div className="flex items-center gap-2">
              <span
                className={`w-2 h-2 rounded-full ${
                  webRunning ? "bg-amber-400 animate-pulse" : web?.status === "error" ? "bg-red-500" : "bg-[#73bf44]"
                }`}
              />
              <span className="text-sm text-neutral-300" data-testid="website-status">
                {webRunning ? "Refreshing…" : web?.status === "error" ? "Error" : "Up to date"}
              </span>
            </div>
          </div>
          {webRunning && (
            <div className="mb-5">
              <div className="h-1.5 w-full rounded-full bg-neutral-800 overflow-hidden">
                <div className="h-full bg-[#73bf44] transition-all" style={{ width: `${web?.progress || 2}%` }} />
              </div>
              <div className="text-xs text-neutral-500 mt-2" data-testid="website-progress-msg">{web?.message || "Working…"}</div>
            </div>
          )}
          <div className="flex justify-end">
            <PrimaryButton onClick={syncWebsite} disabled={webRunning} data-testid="website-sync-btn">
              <span className="inline-flex items-center gap-2">
                <ArrowsClockwise size={16} className={webRunning ? "animate-spin" : ""} />
                {webRunning ? "Refreshing…" : "Refresh website content"}
              </span>
            </PrimaryButton>
          </div>
        </div>

        <ResearchSettingsCard />

        <VoiceSettingsCard />

        <div className="grid md:grid-cols-2 gap-6">
          <Card icon={Robot} title="AI Model">
            <Row label="Assistant" value="Nick" />
            <Row label="Model" value="Claude Sonnet 5.5" />
            <Row label="Sources" value="KB → OTM website → web search → general" />
            <Row label="Vision" value="Enabled (photo analysis)" />
          </Card>
          <Card icon={ShieldCheck} title="Knowledge Controls">
            <p className="text-sm text-neutral-400 leading-relaxed">
              Nick only uses documents and knowledge entries where <span className="text-[#73bf44]">AI access is enabled</span> and status is <span className="text-[#73bf44]">Approved</span>.
              Admin-only material is never exposed to customers. Manage access per item under Documents, Manuals and Knowledge Entries.
            </p>
          </Card>
          <Card icon={Lock} title="Security">
            <Row label="Admin auth" value="Password + JWT" />
            <Row label="Session" value="7 days" />
            <p className="text-xs text-neutral-600 mt-2">Change the admin password via the ADMIN_PASSWORD environment variable.</p>
          </Card>
          <Card icon={Database} title="Data">
            <Row label="Database" value="MongoDB" />
            <Row label="File storage" value="Emergent object storage" />
            <Row label="Documents" value="PDF text auto-extracted" />
          </Card>
        </div>
      </div>
    </div>
  );
}

function Card({ icon: Icon, title, children }) {
  return (
    <div className="bg-[#0F0F0F] border border-neutral-800 rounded-xl p-6">
      <div className="flex items-center gap-2 mb-4">
        <Icon size={20} className="text-[#73bf44]" weight="duotone" />
        <h2 className="font-heading font-semibold text-white">{title}</h2>
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
function Row({ label, value }) {
  return (
    <div className="flex justify-between text-sm border-b border-neutral-800/60 pb-2">
      <span className="text-neutral-500">{label}</span>
      <span className="text-white">{value}</span>
    </div>
  );
}
